import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { Component, type ErrorInfo, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMyTrip } from "@/lib/trip.functions";
import { TripSwitcher } from "@/components/TripSwitcher";
import {
  getMyMedia,
  getUploadRules,
  saveUploadRules,
  discardStuckUpload,
  markStuckUploadFailed,
} from "@/lib/media.functions";
import { isRetryable, retryUpload } from "@/lib/upload/retry";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ImageIcon, VideoIcon, RefreshCw, CheckCircle2, AlertCircle, Clock, Upload, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import { MediaPreviewDrawer, type PreviewItem } from "@/components/MediaPreviewDrawer";
import { RoavaPage, RoavaCard, SettingRow, StatusChip } from "@/components/roava";
import { runSyncNow } from "@/lib/sync/sync-now";
import { Loader2, RefreshCw as RefreshCwIcon } from "lucide-react";
import { toast } from "sonner";
import { useThumbnailUrl } from "@/lib/thumbnails";


function SyncNowButton({ tripId }: { tripId: string | null }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        toast(t("queue.toast_retrying"));
        try {
          const res = await runSyncNow({ tripId });
          if (res.status === "ok") toast.success(t("queue.toast_retried"));
          else if (res.status === "empty") toast(t("queue.toast_all_caught_up"));
          else if (res.status === "busy") toast(t("queue.toast_already"));
          else if (res.status === "unreachable") toast.error(t("queue.toast_waiting_conn"));
          else if (res.status === "partial") toast(t("queue.toast_still_attention"), { description: res.error });
          else toast.error(t("queue.toast_retry_failed"), { description: res.error });
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <RefreshCwIcon className="mr-1.5 size-4" />}
      {t("queue.btn_retry_uploads")}
    </Button>
  );
}

export const Route = createFileRoute("/_authenticated/queue")({
  head: () => pageMeta({ title: 'Upload Queue — Roava by WGP Apps', description: 'Track pending photo and video uploads to your shared trip.', path: '/queue' }),
  component: Queue,
});

type StatusFilter = "all" | "queued" | "uploading" | "uploaded" | "failed";
type TypeFilter = "all" | "photo" | "video";

function Queue() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const tripFn = useServerFn(getMyTrip);
  const mediaFn = useServerFn(getMyMedia);
  const getRulesFn = useServerFn(getUploadRules);
  const saveRulesFn = useServerFn(saveUploadRules);

  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id;

  const { data } = useQuery({
    queryKey: ["my-media", tripId],
    queryFn: () => mediaFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    refetchInterval: 8_000,
  });

  const { data: rules } = useQuery({
    queryKey: ["upload-rules", tripId],
    queryFn: () => getRulesFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
  });

  const retry = useMutation({
    mutationFn: async (id: string) => {
      const res = await retryUpload(id);
      if (!res.ok) {
        if (res.reason === "no-blob" || res.reason === "no-registry") {
          throw new Error("RE_PICK_REQUIRED");
        }
        throw new Error(res.error ?? "Upload failed");
      }
      return res;
    },
    onSuccess: () => {
      toast.success(t("queue.toast_upload_retried"));
      qc.invalidateQueries({ queryKey: ["my-media"] });
    },
    onError: (err: any) => {
      if (err?.message === "RE_PICK_REQUIRED") {
        toast.error(t("queue.toast_repick"));
      } else {
        toast.error(t("queue.toast_retry_failed"), { description: err?.message });
      }
      qc.invalidateQueries({ queryKey: ["my-media"] });
    },
  });

  const discardFn = useServerFn(discardStuckUpload);
  const markFailedFn = useServerFn(markStuckUploadFailed);
  const discard = useMutation({
    mutationFn: async (id: string) => {
      const res = await discardFn({ data: { mediaId: id } });
      if (!res.ok) throw new Error(res.reason);
      return res;
    },
    onSuccess: () => {
      toast.success(t("queue.toast_discarded"));
      qc.invalidateQueries({ queryKey: ["my-media"] });
      qc.invalidateQueries({ queryKey: ["trip-dashboard"] });
    },
    onError: (err: any) => toast.error(t("queue.toast_cant_discard"), { description: err?.message }),
  });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [selected, setSelected] = useState<PreviewItem | null>(null);

  const items: any[] = data?.items ?? [];

  const counts = useMemo(() => {
    const c = { queued: 0, uploading: 0, uploaded: 0, failed: 0 };
    for (const it of items) {
      const s = (it.upload_status ?? "queued") as keyof typeof c;
      if (s in c) c[s]++;
    }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const status = it.upload_status ?? "queued";
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (typeFilter !== "all" && it.media_type !== typeFilter) return false;
      return true;
    });
  }, [items, statusFilter, typeFilter]);

  // Local rules state with debounced save
  const [local, setLocal] = useState<{ wifiOnly: boolean; uploadOriginals: boolean; compressedPreviewsFirst: boolean } | null>(null);
  const hydrated = useRef(false);
  useEffect(() => {
    if (rules && !hydrated.current) {
      setLocal(rules);
      hydrated.current = true;
    }
  }, [rules]);

  const saveRules = useMutation({
    mutationFn: (vals: { wifiOnly: boolean; uploadOriginals: boolean; compressedPreviewsFirst: boolean }) =>
      saveRulesFn({ data: { tripId: tripId!, ...vals } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["upload-rules", tripId] }),
  });

  useEffect(() => {
    if (!local || !tripId || !hydrated.current) return;
    const tm = setTimeout(() => saveRules.mutate(local), 400);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, tripId]);

  // Belt + suspenders: auto-flip server rows that have been "uploading" for
  // >10 min to "failed" when the local blob is gone — so the pending counter
  // doesn't sit at "2 pending" forever after iOS killed a mid-flight upload.
  const sweptRef = useRef(new Set<string>());
  useEffect(() => {
    const now = Date.now();
    const stuck = items.filter((it) => {
      if (it.upload_status !== "uploading") return false;
      const created = it.created_at ? new Date(it.created_at).getTime() : now;
      return now - created > 10 * 60 * 1000;
    });
    for (const it of stuck) {
      if (sweptRef.current.has(it.id)) continue;
      sweptRef.current.add(it.id);
      void (async () => {
        const ageMs = now - (it.created_at ? new Date(it.created_at).getTime() : now);
        const recoverable = it.local_upload_id ? await isRetryable(it.id) : false;
        if (recoverable && ageMs < 60 * 60 * 1000) return;
        try {
          await markFailedFn({ data: { mediaId: it.id } });
          qc.invalidateQueries({ queryKey: ["my-media"] });
          qc.invalidateQueries({ queryKey: ["trip-dashboard"] });
        } catch {
          /* ignore — next visit will retry the sweep */
        }
      })();
    }
  }, [items, markFailedFn, qc]);

  return (
    <>
      <RoavaPage
        title={t("queue.page_title")}
        subtitle={t("queue.subtitle", { queued: counts.queued, uploading: counts.uploading, uploaded: counts.uploaded, failed: counts.failed })}
        action={<TripSwitcher />}
      >
        <div className="flex justify-end mb-3">
          <SyncNowButton tripId={tripId ?? null} />
        </div>
        {/* Filter bar */}
        <div className="-mx-2 px-2 overflow-x-auto mb-4">
          <div className="flex gap-2 w-max pb-1">
            <FilterGroup>
              <Pill active={statusFilter === "all"} onClick={() => setStatusFilter("all")}>{t("queue.filter_all")}</Pill>
              <Pill active={statusFilter === "queued"} onClick={() => setStatusFilter("queued")}>{t("queue.filter_queued")}</Pill>
              <Pill active={statusFilter === "uploading"} onClick={() => setStatusFilter("uploading")}>{t("queue.filter_uploading")}</Pill>
              <Pill active={statusFilter === "uploaded"} onClick={() => setStatusFilter("uploaded")}>{t("queue.filter_uploaded")}</Pill>
              <Pill active={statusFilter === "failed"} onClick={() => setStatusFilter("failed")}>{t("queue.filter_failed")}</Pill>
            </FilterGroup>
            <Divider />
            <FilterGroup>
              <Pill active={typeFilter === "photo"} onClick={() => setTypeFilter(typeFilter === "photo" ? "all" : "photo")}>{t("queue.filter_photos")}</Pill>
              <Pill active={typeFilter === "video"} onClick={() => setTypeFilter(typeFilter === "video" ? "all" : "video")}>{t("queue.filter_videos")}</Pill>
            </FilterGroup>
          </div>
        </div>

        {/* Upload rules */}
        <RoavaCard title={t("queue.rules_title")} className="mb-6">
          <div className="grid">
            <SettingRow
              label={t("queue.rules_wifi_label")}
              description={t("queue.rules_wifi_desc")}
              control={
                <Switch
                  checked={local?.wifiOnly ?? true}
                  onCheckedChange={(v) => setLocal((s) => ({ ...(s ?? { uploadOriginals: true, compressedPreviewsFirst: false, wifiOnly: true }), wifiOnly: v }))}
                />
              }
            />
            <SettingRow
              label={t("queue.rules_originals_label")}
              description={t("queue.rules_originals_desc")}
              control={
                <Switch
                  checked={local?.uploadOriginals ?? true}
                  onCheckedChange={(v) => setLocal((s) => ({ ...(s ?? { uploadOriginals: true, compressedPreviewsFirst: false, wifiOnly: true }), uploadOriginals: v }))}
                />
              }
            />
            <SettingRow
              label={t("queue.rules_compressed_label")}
              description={t("queue.rules_compressed_desc")}
              control={
                <Switch
                  checked={local?.compressedPreviewsFirst ?? false}
                  onCheckedChange={(v) => setLocal((s) => ({ ...(s ?? { uploadOriginals: true, compressedPreviewsFirst: false, wifiOnly: true }), compressedPreviewsFirst: v }))}
                />
              }
            />
          </div>
        </RoavaCard>

        {/* List */}
        <h2 className="text-xs font-semibold uppercase tracking-wider text-[var(--roava-text-muted)] mb-2">
          {t("queue.items_heading", { count: filtered.length })}
        </h2>
        <div className="grid gap-2">
          {filtered.length === 0 && (
            <p className="text-sm text-[var(--roava-text-muted)] rounded-xl border border-dashed border-[var(--roava-border)] p-6 text-center">
              {t("queue.empty_filter")}
            </p>
          )}
          <IncrementalList
            items={filtered}
            renderItem={(it) => (
              <RowErrorBoundary key={it.id}>
                <QueueRow
                  item={it}
                  onRetry={() => retry.mutate(it.id)}
                  retrying={retry.isPending}
                  onDiscard={() => discard.mutate(it.id)}
                  discarding={discard.isPending}
                  onOpen={() => setSelected(it)}
                />
              </RowErrorBoundary>
            )}
          />
        </div>

      </RoavaPage>
      <MediaPreviewDrawer item={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function FilterGroup({ children }: { children: React.ReactNode }) {
  return <div className="flex gap-1.5">{children}</div>;
}
function Divider() {
  return <div className="w-px bg-border mx-1 self-stretch" />;
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "text-xs font-medium px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors",
        active
          ? "bg-[var(--roava-blue)] text-[var(--roava-blue-foreground)] border-[var(--roava-blue)]"
          : "bg-[var(--roava-card)] text-[var(--roava-text)] border-[var(--roava-border)] hover:bg-[var(--roava-gray)]/50",
      )}
    >
      {children}
    </button>
  );
}

function QueueRow({ item, onRetry, retrying, onDiscard, discarding, onOpen }: { item: any; onRetry: () => void; retrying: boolean; onDiscard: () => void; discarding: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  const isVideo = item.media_type === "video";
  const status = (item.upload_status ?? "queued") as "queued" | "uploading" | "uploaded" | "failed";
  const captured = item.captured_at ?? item.created_at;
  const createdMs = item.created_at ? new Date(item.created_at).getTime() : Date.now();
  const isStuck =
    (status === "uploading" || status === "queued") &&
    Date.now() - createdMs > 10 * 60 * 1000;

  const [retryable, setRetryable] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    if (status !== "failed" && !isStuck) {
      setRetryable(null);
      return;
    }
    if (!item.local_upload_id) {
      setRetryable(false);
      return;
    }
    isRetryable(item.id).then((ok) => {
      if (alive) setRetryable(ok);
    });
    return () => {
      alive = false;
    };
  }, [item.id, item.local_upload_id, status, isStuck]);

  const progress =
    status === "uploaded" ? 100
    : status === "failed" ? 100
    : 0;
  const indeterminate = status === "uploading" && !isStuck;
  const canDiscard = isStuck || status === "failed";
  const typeLabel = isVideo ? t("queue.row_video") : t("queue.row_photo");

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <button type="button" onClick={onOpen} className="w-full text-left flex items-center gap-3 -m-1 p-1 rounded-lg hover:bg-muted/40 active:bg-muted/60 transition">
        <div className="size-14 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
          <QueueThumb item={item} isVideo={isVideo} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              {typeLabel}
            </span>
            <StatusBadge status={status} stuck={isStuck} />
          </div>
          <p className="text-sm font-medium truncate">
            {item.original_filename ?? t("queue.row_label_id", { type: typeLabel, id: item.id.slice(0, 6) })}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatTime(captured, t)} · {formatBytes(item.file_size_bytes)}
          </p>
        </div>

        {(status === "failed" || isStuck) && retryable === true && (
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onRetry(); }} disabled={retrying} className="rounded-xl shrink-0">
            <RefreshCw className={cn("size-4 mr-1", retrying && "animate-spin")} /> {t("queue.row_retry")}
          </Button>
        )}
        {status === "failed" && retryable === false && (
          <Link
            to="/upload"
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 inline-flex items-center gap-1 rounded-xl border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-muted"
          >
            <Camera className="size-3.5" /> {t("queue.row_repick")}
          </Link>
        )}
      </button>

      {(status === "failed" || isStuck) && retryable === false && (
        <p className="mt-2 text-xs text-muted-foreground">
          {isStuck ? t("queue.row_stuck_msg") : t("queue.row_unavail_msg")}
        </p>
      )}

      {canDiscard && (
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            disabled={discarding}
            onClick={onDiscard}
            className="h-7 text-xs text-muted-foreground hover:text-destructive"
          >
            {t("queue.row_discard")}
          </Button>
        </div>
      )}


      <div className="mt-3">
        {indeterminate ? (
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full w-1/3 rounded-full bg-primary animate-[queue-indeterminate_1.2s_ease-in-out_infinite]" />
          </div>
        ) : (
          <Progress
            value={progress}
            className={cn(
              "h-1.5",
              status === "failed" && "[&>div]:bg-destructive",
              status === "uploaded" && "[&>div]:bg-emerald-500",
            )}
          />
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, stuck }: { status: "queued" | "uploading" | "uploaded" | "failed"; stuck?: boolean }) {
  const { t } = useTranslation();
  if (stuck && (status === "uploading" || status === "queued")) {
    return (
      <StatusChip variant="warning" icon={<AlertCircle className="size-3" />}>{t("queue.status_stuck")}</StatusChip>
    );
  }
  const map = {
    queued: { Icon: Clock, label: t("queue.status_queued"), variant: "neutral" as const },
    uploading: { Icon: Upload, label: t("queue.status_uploading"), variant: "info" as const },
    uploaded: { Icon: CheckCircle2, label: t("queue.status_uploaded"), variant: "success" as const },
    failed: { Icon: AlertCircle, label: t("queue.status_failed"), variant: "danger" as const },
  };
  const { Icon, label, variant } = map[status];
  return (
    <StatusChip variant={variant} icon={<Icon className="size-3" />}>{label}</StatusChip>
  );
}

function formatBytes(n?: number | null) {
  if (!n || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatTime(iso: string | null | undefined, t: ReturnType<typeof useTranslation>["t"]) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return t("queue.format_just_now");
  if (diff < 3600) return t("queue.format_m_ago", { n: Math.floor(diff / 60) });
  if (diff < 86_400) return t("queue.format_h_ago", { n: Math.floor(diff / 3600) });
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function QueueThumb({ item, isVideo }: { item: any; isVideo: boolean }) {
  const { url: signed } = useThumbnailUrl(
    !isVideo ? item.id : null,
    200,
  );
  if (isVideo) return <VideoIcon className="size-6 text-muted-foreground" />;
  const src: string | null =
    (typeof item.thumb_url === "string" && item.thumb_url) || signed;
  if (!src) return <ImageIcon className="size-6 text-muted-foreground" />;
  return (
    <img
      src={src}
      alt=""
      width={56}
      height={56}
      loading="lazy"
      decoding="async"
      className="size-full object-cover"
    />
  );
}

function IncrementalList<T extends { id: string }>({
  items,
  renderItem,
  initial = 30,
  step = 30,
}: {
  items: T[];
  renderItem: (item: T) => ReactNode;
  initial?: number;
  step?: number;
}) {
  const { t } = useTranslation();
  const [count, setCount] = useState(initial);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCount((c) => Math.min(Math.max(c, initial), Math.max(items.length, initial)));
  }, [items.length, initial]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (count >= items.length) return;
    if (typeof IntersectionObserver === "undefined") {
      setCount(items.length);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setCount((c) => Math.min(items.length, c + step));
          }
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [count, items.length, step]);

  const visible = items.slice(0, count);
  return (
    <>
      {visible.map((it) => renderItem(it))}
      {count < items.length && (
        <div ref={sentinelRef} className="py-4 text-center text-xs text-muted-foreground">
          {t("queue.loading_more", { count, total: items.length })}
        </div>
      )}
    </>
  );
}

class RowErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[queue-row] render failed", error, info);
  }
  render() {
    if (this.state.failed) {
      return (
        <RowErrorFallback />
      );
    }
    return this.props.children;
  }
}

function RowErrorFallback() {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
      {t("queue.row_error")}
    </div>
  );
}
